import React, { useState } from "react";
import { loader } from "graphql.macro";
import { useMutation } from "@apollo/client";
import { Box, Button, CircularProgress, Alert } from "@mui/material";
import { FormattedMessage } from "react-intl";

const CREATE_BILLING_PORTAL_SESSION = loader(
  "../../schema/mutation/CreateBillingPortalSession.graphql"
);

interface PortalData {
  createBillingPortalSession: { url: string };
}

interface Props {
  organizationId: string;
}

// Owner-only button that opens the Stripe Billing Portal (cancel, change the
// payment method or the VAT number, invoice history). The plan, Voice Trust+ and
// the seats are changed in the app, see Billing/index.tsx.
const ManageBillingButton: React.FC<Props> = ({ organizationId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createBillingPortalSession] = useMutation<PortalData>(
    CREATE_BILLING_PORTAL_SESSION
  );

  const handleClick = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await createBillingPortalSession({
        variables: { organizationId },
      });
      const url = data?.createBillingPortalSession.url;
      if (url) {
        window.location.href = url;
      } else {
        setLoading(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setLoading(false);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleClick}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <FormattedMessage
            id="billing.manage-in-portal"
            defaultMessage="Invoices, payment method, cancellation"
          />
        )}
      </Button>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default ManageBillingButton;
